import React, {ReactNode, useEffect, useMemo} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';

import {
    filtersChanged,
    filtersReset,
    pageNumberChanged,
    refetchWithSortByDescDate,
    sortingsChanged,
    stateReset,
    tableState$,
} from 'models/documents/gds';
import {GdsGridFormattedRow, GdsGridRawRow} from 'types';

interface Props {
    configFunc: Array<any>;
    dataMapper: (data: GdsGridRawRow[] | undefined, t: TFunction) => GdsGridFormattedRow[];
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
    headerControls?: ReactNode;
    headerControlsAlignItems?: string;
}

export const GDTable = ({
    configFunc,
    dataMapper,
    headerControls,
    exportConfig,
    headerControlsAlignItems,
}: Props) => {
    const {t} = useTranslation();
    const {data, pageNumber, isLoading, filters, sortings} = useStore(tableState$);
    useEffect(() => {
        stateReset();
        refetchWithSortByDescDate();
    }, []);

    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc,
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, filters, sortings],
    );

    const mappedData = React.useMemo(() => dataMapper(data?.data, t), [dataMapper, data, t]);

    return (
        <>
            <Table
                columns={columns}
                data={mappedData}
                pageIndex={pageNumber}
                pageCount={data?.totalPages}
                onPageChange={pageNumberChanged}
                isLoading={isLoading}
                userConfig={{
                    gridName: 'gdsGrid',
                    saveConfig: saveGridConfig,
                    getConfig: fetchGridConfig,
                }}
                headerControls={headerControls}
                exportConfig={exportConfig}
                headerControlsAlignItems={headerControlsAlignItems}
            />
        </>
    );
};
